import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Alert, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemIcon, ListItemText, Chip, Tabs, Tab, Accordion,
  AccordionSummary, AccordionDetails, IconButton, Divider,
  TextField, Link, Stepper, Step, StepLabel
} from '@mui/material';
import {
  Computer, Android, Apple, QrCode, Download, ContentCopy,
  Launch, CheckCircle, Cable, Settings, Code, Close, ExpandMore
} from '@mui/icons-material';
import QRCode from 'qrcode';

const ArduinoProgrammerUniversal = ({ binId, arduinoCode, onClose, open }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    if (open && binId) {
      generateQRCode();
      setDownloadUrl(`${window.location.origin}/api/bins/${binId}/arduino-code`);
    }
  }, [open, binId]);

  const generateQRCode = async () => {
    try {
      const url = `${window.location.origin}/api/bins/${binId}/arduino-code`;
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrDataUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const downloadArduinoCode = () => {
    const blob = new Blob([arduinoCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${binId}_arduino_code.ino`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const programmingMethods = [
    { label: 'Computer (Arduino IDE)', icon: <Computer /> },
    { label: 'Android Device', icon: <Android /> },
    { label: 'iOS Device', icon: <Apple /> },
    { label: 'QR Code Access', icon: <QrCode /> }
  ];

  const renderComputerMethod = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Best for: Laptops, Desktops with Arduino IDE</Typography>
      </Alert>
      
      <Stepper orientation="vertical" sx={{ mb: 3 }}>
        <Step active>
          <StepLabel>Download Arduino IDE</StepLabel>
          <Box sx={{ ml: 4, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Download from: arduino.cc/downloads
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Launch />}
              onClick={() => window.open('https://arduino.cc/downloads', '_blank')}
            >
              Open Arduino Website
            </Button>
          </Box>
        </Step>
        
        <Step active>
          <StepLabel>Download Arduino Code</StepLabel>
          <Box sx={{ ml: 4, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={downloadArduinoCode}
              sx={{ mr: 1 }}
            >
              Download {binId}.ino
            </Button>
            <Button
              variant="outlined"
              startIcon={copied ? <CheckCircle /> : <ContentCopy />}
              onClick={() => copyToClipboard(arduinoCode)}
              color={copied ? "success" : "primary"}
            >
              {copied ? 'Copied!' : 'Copy Code'}
            </Button>
          </Box>
        </Step>
        
        <Step active>
          <StepLabel>Connect Arduino</StepLabel>
          <Box sx={{ ml: 4, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Connect Arduino Uno/Nano via USB cable to your computer
            </Typography>
          </Box>
        </Step>
        
        <Step active>
          <StepLabel>Upload Code</StepLabel>
          <Box sx={{ ml: 4, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              1. Open the downloaded .ino file in Arduino IDE<br/>
              2. Select correct Board: Arduino Uno/Nano<br/>
              3. Select correct Port (COM port on Windows, /dev/tty on Mac/Linux)<br/>
              4. Click Upload button (→)
            </Typography>
          </Box>
        </Step>
      </Stepper>
    </Box>
  );

  const renderAndroidMethod = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Best for: Android phones/tablets with OTG support</Typography>
      </Alert>
      
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recommended Apps:</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Android /></ListItemIcon>
              <ListItemText 
                primary="Arduino IDE (Official)"
                secondary="Free - Full Arduino IDE for Android"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open('https://play.google.com/store/apps/details?id=cc.arduino.IDE2', '_blank')}
              >
                Get App
              </Button>
            </ListItem>
            <ListItem>
              <ListItemIcon><Android /></ListItemIcon>
              <ListItemText 
                primary="ArduinoDroid"
                secondary="Alternative IDE with OTG support"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open('https://play.google.com/store/apps/details?id=name.antonsmirnov.android.arduinodroid2', '_blank')}
              >
                Get App
              </Button>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Step-by-Step Instructions</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div">
            1. <strong>Install Arduino IDE app</strong> from Google Play Store<br/>
            2. <strong>Enable OTG</strong> in your phone settings<br/>
            3. <strong>Connect Arduino</strong> using USB OTG cable<br/>
            4. <strong>Download code</strong> using the button below<br/>
            5. <strong>Open code</strong> in Arduino IDE app<br/>
            6. <strong>Select board</strong> and port in app settings<br/>
            7. <strong>Upload code</strong> to Arduino
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={downloadArduinoCode}
            >
              Download Code for Mobile
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderiOSMethod = () => (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Limited Support: iOS has restrictions on USB connections</Typography>
      </Alert>
      
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recommended Approach:</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><Apple /></ListItemIcon>
              <ListItemText 
                primary="Use iPad with USB-C"
                secondary="iPad Pro/Air with USB-C can connect to Arduino"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Code /></ListItemIcon>
              <ListItemText 
                primary="Blynk IoT app"
                secondary="Alternative for IoT programming"
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => window.open('https://apps.apple.com/app/blynk-iot/id808760481', '_blank')}
              >
                Get App
              </Button>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Alternative: Use Computer</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2">
            For best results with iOS devices, we recommend:
            <br/>• Use a computer with Arduino IDE
            <br/>• Download the code using QR code method
            <br/>• Transfer to computer via AirDrop or email
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<QrCode />}
              onClick={() => setSelectedTab(3)}
            >
              Use QR Code Method
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderQRMethod = () => (
    <Box sx={{ textAlign: 'center' }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Universal: Works on any device with camera</Typography>
      </Alert>
      
      <Card sx={{ mb: 2, p: 2 }}>
        <Typography variant="h6" gutterBottom>Scan QR Code</Typography>
        {qrCodeDataUrl && (
          <Box sx={{ mb: 2 }}>
            <img 
              src={qrCodeDataUrl} 
              alt="Arduino Code QR" 
              style={{ maxWidth: '200px', height: 'auto' }}
            />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Scan with any device to access the Arduino code
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={downloadUrl}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton onClick={() => copyToClipboard(downloadUrl)}>
                {copied ? <CheckCircle color="success" /> : <ContentCopy />}
              </IconButton>
            )
          }}
          sx={{ mt: 1 }}
        />
      </Card>

      <Typography variant="h6" gutterBottom>How to Use:</Typography>
      <List dense>
        <ListItem>
          <ListItemText 
            primary="1. Scan QR code with phone camera"
            secondary="Opens the Arduino code in browser"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="2. Copy or download the code"
            secondary="Save to your preferred programming device"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="3. Use any Arduino programming method"
            secondary="Computer IDE, mobile app, or online editor"
          />
        </ListItem>
      </List>
    </Box>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0: return renderComputerMethod();
      case 1: return renderAndroidMethod();
      case 2: return renderiOSMethod();
      case 3: return renderQRMethod();
      default: return renderComputerMethod();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Arduino Programming</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Bin ID: {binId}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {programmingMethods.map((method, index) => (
              <Tab 
                key={index}
                icon={method.icon} 
                label={method.label}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
        
        {renderTabContent()}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={() => onClose(true)} 
          variant="contained"
          startIcon={<CheckCircle />}
        >
          Mark as Programmed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArduinoProgrammerUniversal;
